import { useAtomValue, useSetAtom } from "jotai";
import { RefObject, useCallback, useEffect, useMemo, useRef } from "react";
import type { AttachmentFileData } from "shared/types";
import {
  addChatFileAttachments,
  chatFileAttachments,
  chatReplyToMessage,
  chatSendMode,
  clearChatFileAttachments,
  resetChatSendMode,
  setChatReplyToMessage,
  setChatSendMode,
} from "../store";
import type { ChatMessageInputRef } from "../TipTapChatInput";
import {
  isDraftEmpty,
  readChatDraft,
  removeChatDraft,
  toPersistedAttachments,
  writeChatDraft,
} from "../utils/chatDraftStorage";

const DRAFT_DEBOUNCE_MS = 400;

interface UseChatDraftParams {
  serviceId: string;
  editorRef: RefObject<ChatMessageInputRef | null>;
}

/**
 * Persists the chat composer draft (text + completed attachments + reply +
 * send mode) per serviceId in localStorage so it survives a page refresh.
 * Logout clears it for free (all logout paths call localStorage.clear()).
 */
export const useChatDraft = ({ serviceId, editorRef }: UseChatDraftParams) => {
  const fileAttachments = useAtomValue(chatFileAttachments);
  const replyTo = useAtomValue(chatReplyToMessage);
  const sendMode = useAtomValue(chatSendMode);

  const addFiles = useSetAtom(addChatFileAttachments);
  const clearFiles = useSetAtom(clearChatFileAttachments);
  const setReply = useSetAtom(setChatReplyToMessage);
  const setMode = useSetAtom(setChatSendMode);
  const resetMode = useSetAtom(resetChatSendMode);

  // Latest values for the debounced writer to read without re-creating it.
  const filesRef = useRef(fileAttachments);
  filesRef.current = fileAttachments;
  const replyRef = useRef(replyTo);
  replyRef.current = replyTo;
  const modeRef = useRef(sendMode);
  modeRef.current = sendMode;

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initial editor content for the current service, read synchronously so it
  // is available on first render (editor content is only applied at creation).
  const initialHtml = useMemo(
    () => readChatDraft(serviceId)?.html ?? "",
    [serviceId],
  );

  // ─── Rehydrate composer context atoms on mount / service switch ───
  useEffect(() => {
    // Reset first so an in-place service switch never leaks the previous draft.
    clearFiles();
    setReply(null);
    resetMode();

    const draft = readChatDraft(serviceId);
    if (!draft) return;

    if (draft.attachments.length > 0) {
      addFiles(
        draft.attachments.map((a) => ({
          ...a,
          file: null,
        })) as AttachmentFileData[],
      );
    }
    if (draft.replyTo) setReply(draft.replyTo);
    setMode(draft.sendMode);
  }, [serviceId, addFiles, clearFiles, setReply, setMode, resetMode]);

  // ─── Debounced persist ────────────────────────────────────────────
  const persistDraft = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      const html = editorRef.current?.getEditor()?.getHTML() ?? "";
      const attachments = toPersistedAttachments(filesRef.current);
      const reply = replyRef.current;

      if (isDraftEmpty(html, attachments, reply)) {
        removeChatDraft(serviceId);
        return;
      }

      writeChatDraft(serviceId, {
        html,
        attachments,
        replyTo: reply,
        sendMode: modeRef.current,
        updatedAt: Date.now(),
      });
    }, DRAFT_DEBOUNCE_MS);
  }, [serviceId, editorRef]);

  const clearDraft = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    removeChatDraft(serviceId);
  }, [serviceId]);

  // Cancel a pending write on unmount.
  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  return { initialHtml, persistDraft, clearDraft };
};
